import { useEffect, useRef, useState } from 'react';
import type { Material } from '../type/Material';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { useTranslation } from '@/hooks/useTranslation';

const DEFAULT_MATERIAL = (companyName: string): Material => ({
  name: '',
  companyName,
  isoCategory: '',
  designation: null,
  description: null,
  archived: false,
});

export function useMaterialManager(
  companyName: string,
  traceabilityApiClient: TraceabilityApiClient | null
) {
  const { t } = useTranslation();

  const [materials, setMaterials] = useState<Material[]>([]);
  const [formMaterial, setFormMaterial] = useState<Material>(() => DEFAULT_MATERIAL(companyName));
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const writeControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      writeControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setArchiveConfirm(false);
  }, [selectedId]);

  useEffect(() => {
    if (!traceabilityApiClient) return;
    const ac = new AbortController();
    setIsLoading(true);
    setFormError(null);

    traceabilityApiClient
      .materialReadAll({}, { signal: ac.signal })
      .then((response) => {
        if (ac.signal.aborted) return;
        if (response.ok && response.data?.materials) {
          setMaterials(
            response.data.materials.map((item: { material: Material }) => item.material)
          );
        } else {
          if (!response.ok && response.code === 401) return;
          setFormError(
            response.ok
              ? t('traceability.materialManager.errors.noData')
              : response.message || t('traceability.materialManager.errors.fetchFailed')
          );
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.error('Failed to fetch materials:', err);
        setFormError(t('traceability.materialManager.errors.fetchFailed'));
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoading(false);
      });

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceabilityApiClient]);

  const refreshMaterials = async (signal: AbortSignal) => {
    if (!traceabilityApiClient) return;
    setIsLoading(true);
    try {
      const response = await traceabilityApiClient.materialReadAll({});
      if (signal.aborted) return;
      if (response.ok && response.data?.materials) {
        setMaterials(
          response.data.materials.map((item: { material: Material }) => item.material)
        );
      }
    } catch (err: unknown) {
      console.error('Failed to refresh materials:', err);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  const handleAddOrUpdate = async () => {
    if (!traceabilityApiClient) {
      setFormError(t('traceability.materialManager.errors.apiNotReady'));
      return;
    }
    const trimmedName = formMaterial.name.trim();
    if (!trimmedName) {
      setFormError(t('traceability.materialManager.errors.nameRequired'));
      return;
    }
    if (!formMaterial.isoCategory) {
      setFormError(t('traceability.materialManager.errors.isoCategoryRequired'));
      return;
    }

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;
    setIsSaving(true);
    setFormError(null);

    try {
      const payload: Material = {
        ...(formMaterial.id !== undefined && { id: formMaterial.id }),
        name: trimmedName,
        companyName,
        isoCategory: formMaterial.isoCategory,
        designation: formMaterial.designation?.trim() || null,
        description: formMaterial.description?.trim() || null,
        archived: formMaterial.id !== undefined ? formMaterial.archived : false,
      };

      const response = await traceabilityApiClient.materialWrite(
        { material: payload },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;

      if (response.ok) {
        const savedMaterial = { ...payload, id: response.data.id };
        await refreshMaterials(ac.signal);
        if (ac.signal.aborted) return;
        setFormMaterial(savedMaterial);
        setSelectedId(response.data.id);
      } else {
        if (response.code === 401) return;
        setFormError(response.message || t('traceability.materialManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to save material:', error);
      setFormError(t('traceability.materialManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveConfirm || !traceabilityApiClient || selectedId === undefined) return;
    const selectedMaterial = materials.find((m) => m.id === selectedId);
    if (!selectedMaterial) return;

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;
    setIsArchiving(true);
    setFormError(null);

    try {
      const response = await traceabilityApiClient.materialWrite(
        { material: { ...selectedMaterial, archived: true } },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;

      if (response.ok) {
        setArchiveConfirm(false);
        await refreshMaterials(ac.signal);
        if (ac.signal.aborted) return;
        setSelectedId(undefined);
        setFormMaterial(DEFAULT_MATERIAL(companyName));
      } else {
        if (response.code === 401) return;
        setFormError(response.message || t('traceability.materialManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to archive material:', error);
      setFormError(t('traceability.materialManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!traceabilityApiClient || selectedId === undefined) return;
    const selectedMaterial = materials.find((m) => m.id === selectedId);
    if (!selectedMaterial) return;

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;
    setIsArchiving(true);
    setFormError(null);

    try {
      const response = await traceabilityApiClient.materialWrite(
        { material: { ...selectedMaterial, archived: false } },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;

      if (response.ok) {
        await refreshMaterials(ac.signal);
        if (ac.signal.aborted) return;
        setSelectedId(undefined);
        setFormMaterial(DEFAULT_MATERIAL(companyName));
      } else {
        if (response.code === 401) return;
        setFormError(response.message || t('traceability.materialManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to restore material:', error);
      setFormError(t('traceability.materialManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsArchiving(false);
    }
  };

  const handleSelectMaterial = (material: Material) => {
    setFormError(null);
    setFormMaterial(material);
    setSelectedId(material.id);
  };

  const handleNewMaterial = () => {
    setFormError(null);
    setFormMaterial(DEFAULT_MATERIAL(companyName));
    setSelectedId(undefined);
  };

  const selectedMaterial = materials.find((m) => m.id === selectedId);
  const isEditing = formMaterial.id !== undefined;
  const visibleMaterials = showArchived ? materials : materials.filter((m) => !m.archived);
  const activeMaterials = materials.filter((m) => !m.archived);

  return {
    materials,
    formMaterial,
    setFormMaterial,
    selectedId,
    formError,
    isLoading,
    isSaving,
    isArchiving,
    archiveConfirm,
    setArchiveConfirm,
    showArchived,
    setShowArchived,
    selectedMaterial,
    isEditing,
    visibleMaterials,
    activeMaterials,
    handleAddOrUpdate,
    handleArchive,
    handleRestore,
    handleSelectMaterial,
    handleNewMaterial,
  };
}
